<template>
    <div :class="{ 'opacity-75': isLoading }" class="qa-config-item">
        <div class="flex items-center justify-between">
            <div class="title">{{ title }}</div>
            <action-menu
                v-if="
                    hasAnyPermission(['quality_assurance.update', 'quality_assurance.delete']) &&
                    (outputType !== 'work_order' || hasAnyPermission('quality_assurance.preventative_maintenance_schedule.manage'))
                "
                :is-loading="isLoading"
                placement="bottom-end"
            >
                <dropdown-item v-if="editable && hasAnyPermission('quality_assurance.update')" @click.stop="$emit('edit', id)">
                    <div class="flex w-100 items-center justify-between">
                        {{ trans('general.action.edit') }}
                        <icon icon="cil-pencil" />
                    </div>
                </dropdown-item>
                <dropdown-item v-if="hasAnyPermission('quality_assurance.delete')" @click.stop="$emit('delete', id)">
                    <div class="flex w-100 items-center justify-between">
                        {{ trans('general.action.delete') }}
                        <icon icon="cil-trash" />
                    </div>
                </dropdown-item>
            </action-menu>
        </div>
        <div class="details grid">
            <!-- Config name -->
            <div v-if="outputType !== 'work_order'" class="sub-grid">
                <div class="label">{{ trans('qa.configuration.item.label.test') }}:</div>
                <div class="value truncate">
                    {{ configName || trans('general.not_available') }}
                </div>
            </div>
            <!-- Not before / Due date -->
            <div v-if="isFutureScheduledItem || !scheduledItem?.due_at" class="sub-grid">
                <div class="label">{{ trans('qa.configuration.item.label.not_before') }}:</div>
                <div class="value flex flex-wrap gap-x-1 truncate">
                    <template v-if="plannedNotBefore">
                        <div class="truncate">{{ dateTimeMedium(plannedNotBefore, currentLocale, timezone) }}</div>
                        <div class="opacity-75">({{ trans('general.planned') }})</div>
                    </template>
                    <template v-else>
                        <div class="truncate">
                            {{
                                dateTimeMedium(scheduledItem?.not_before, currentLocale, scheduledItem?.asset?.timezone?.identifier) ||
                                trans('general.not_available')
                            }}
                        </div>
                        <div v-if="scheduledItem?.not_before" class="opacity-75">({{ scheduledItem?.asset?.timezone?.name }})</div>
                    </template>
                </div>
            </div>
            <div v-else class="sub-grid">
                <div class="label">{{ trans('qa.configuration.item.label.next_due') }}:</div>
                <div class="value truncate">
                    <div class="truncate">
                        {{
                            dateTimeMedium(scheduledItem?.due_at, currentLocale, scheduledItem?.asset?.timezone?.identifier) ||
                            trans('general.not_available')
                        }}
                    </div>
                    <div v-if="scheduledItem?.due_at" class="text-muted">({{ scheduledItem?.asset?.timezone?.name }})</div>
                </div>
            </div>
            <!-- Repeats -->
            <div class="sub-grid">
                <div class="label">{{ trans('qa.configuration.item.label.repeats') }}:</div>
                <div class="value flex gap-1">
                    <span :class="{ 'line-through': isPaused }" v-html="repeats || trans('general.not_available')" />
                    <span v-if="isPaused">({{ trans('general.paused') }})</span>
                </div>
            </div>
            <!-- SLA link -->
            <div v-if="serviceLevelAgreement" class="sub-grid">
                <div class="label">{{ trans('qa.configuration.item.label.sla') }}:</div>
                <div class="value flex flex-wrap items-center gap-x-1 truncate">
                    <span class="truncate">{{ serviceLevelAgreement.maintainerName }} · {{ serviceLevelAgreement.typeLabel }}</span>
                    <span v-if="serviceLevelAgreement.expired" class="text-danger">({{ trans('general.expired') }})</span>
                </div>
            </div>
            <!-- Ticket ID -->
            <div class="sub-grid">
                <div class="label">{{ trans('qa.configuration.item.label.ticket_id') }}:</div>
                <div class="value flex flex-wrap items-center gap-x-1 truncate">
                    <a
                        v-if="scheduledItem?.url"
                        :href="scheduledItem.url"
                        class="relative inline-flex truncate text-[#2563eb] no-underline hover:underline"
                        @click.stop.prevent="openScheduledItemDetails(scheduledItem)"
                        >{{ scheduledItem?.code || scheduledItem?.id }}</a
                    >
                    <span v-else class="truncate text-muted">{{
                        scheduledItem?.code || scheduledItem?.id || (plannedNotBefore ? trans('general.planned') : trans('general.not_available'))
                    }}</span>
                    <span
                        v-if="scheduledItem?.status?.value === 'done'"
                        :title="dateTimeMedium(scheduledItem?.resolved_at, currentLocale)"
                        class="text-success"
                        ><icon name="cil-check-circle"
                    /></span>
                    <span v-if="isFutureScheduledItem" class="text-muted opacity-75"
                        >({{ scheduledItemAvailableIn(scheduledItem, currentLocale) }})</span
                    >
                    <span v-if="scheduledItem?.is_overdue" class="text-danger">({{ trans('general.overdue') }})</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { trans } from '@/mixins/i18n';
import ActionMenu from '@/Components/ActionMenu.vue';
import DropdownItem from '@/Components/DropdownItem.vue';
import dateTimeMedium from '@/filters/dateTimeMedium';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import { DateTime } from 'luxon';
import { upcomingOccurrences } from '@/Utils/recurrenceOccurrences';
import { visitModal } from '@/Utils/visitModal';
import { scheduledItemAvailableIn } from './utils.js';

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    title: String,
    scheduledItem: Object,
    configName: String,
    repeats: String,
    isLoading: Boolean,
    isPaused: Boolean,
    outputType: {
        type: String,
        default: 'task',
    },
    startDate: String,
    // Viewed asset's timezone identifier; the projection is computed and
    // rendered in it so it matches the real scheduled item's frame.
    timezone: {
        type: String,
        default: null,
    },
    recurrence: {
        type: Object,
        default: null,
    },
    editable: {
        type: Boolean,
        default: false,
    },
    serviceLevelAgreement: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['edit', 'delete']);

const currentLocale = computed(() => usePage().props.locale);

const isFutureScheduledItem = computed(() => {
    const item = props.scheduledItem;
    if (!item) {
        return false;
    }
    return item.not_before && new Date(item.not_before) > new Date();
});

// Project the next occurrence when the scheduler hasn't created a task yet, so
// the card shows when work is coming instead of "N/A". A real scheduled item
// always wins, a paused schedule shows nothing (we can't predict when it
// resumes), and an expired SLA shows nothing (no more work orders are coming).
// This mirrors the backend cadence but can't see operability/SLA pauses, so
// it's surfaced as a "(planned)" estimate, not a firm date. Computed in the
// asset's timezone so the projected calendar day matches the eventual task.
const plannedNotBefore = computed(() => {
    if (props.scheduledItem || props.isPaused || !props.startDate || props.serviceLevelAgreement?.expired) {
        return null;
    }

    const zone = props.timezone || undefined;
    const start = DateTime.fromISO(props.startDate, { zone });
    if (!start.isValid) {
        return null;
    }

    const now = DateTime.now().setZone(zone);
    const projected =
        !props.recurrence || props.recurrence.repeat === 'once' ? start : (upcomingOccurrences(start, props.recurrence, now, 1)[0] ?? null);

    // A projection in the past is not "planned" work: a once-off that won't
    // recur, or a cadence bucket whose open moment has already passed. The
    // scheduler will surface those as real (overdue/active) items, so show
    // nothing rather than label a past date "(planned)".
    if (!projected || projected < now) {
        return null;
    }

    return projected;
});

const openScheduledItemDetails = (item) => {
    visitModal(item.url);
};
</script>

<style scoped>
.qa-config-item {
    background: white;
    border-radius: 0.5rem;
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    container-type: inline-size;
}

.edit-button {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
    border: none;
    background: #e5eced;
    color: #2c54ea;
    font-size: 0.75rem;
    line-height: 0.75rem;
    transition: all 0.2s;
}

.edit-button:hover {
    background: #f3f4f6;
}

.grid {
    margin-top: 1.25rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    align-items: start;
}

.sub-grid {
    display: grid;
    grid-template-columns: 30% 1fr;
    gap: 0.75rem;
    align-items: start;
}

.label {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    overflow-wrap: break-word;
}

.value {
    color: #8490a1;
}

@container (max-width: 400px) {
    .grid {
        grid-template-columns: 1fr;
        gap: 0.25rem;
    }
    .sub-grid {
        grid-template-columns: 6rem 1fr;
    }
}

:deep(.dropdown-menu) {
    min-width: 120px !important;
    font-size: 0.875rem !important;
    line-height: 0.875rem !important;
}
</style>
